/**
 * RowMetadataStore — per-row metadata keyed by `RowLocalId`.
 *
 * Holds every per-row property: first-class row fields
 * (`label`, `heightPx`), layout-specific runtime config
 * (`filter`, `canvas`), and the free-form `attributes` bag that
 * mirrors the `attributes JSONB` column on `SheetRowDb`. One store
 * covers all per-row metadata — there's no dedicated attribute
 * store; readers narrow the JSON bag via `ModelRow.attributes()`
 * or `attributesAs(decode)`.
 *
 * Emits through the shared `InvalidationSink` using lona-js's
 * canonical vocabulary:
 *   - `heightPx` changes → `RowHeightChanged(rowId)`
 *   - `label`    changes → `RowLegendChanged(rowId)`
 *   - other fields        → `RowDataChanged(rowId, field)`
 *
 * No-op patches skip emission.
 */

import type { Option } from "../core/option.ts";
import { DependencyAspect } from "@tento-core/dependency-graph";

import {
  Actor,
  type InvalidationSink,
  RowDataChanged,
  RowHeightChanged,
  RowLegendChanged,
} from "../tx/invalidation.ts";
import { LedgerKeys } from "./ledger-keys.ts";
import type { LedgerBumper } from "./ledger-keys.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { Graph } from "./graph-types.ts";
import type { Json, JsonObject } from "../core/json.ts";
import { frozenCopy } from "../core/frozen-copy.ts";

/**
 * One row's metadata in the rows-side model: its label, height, layout config and `attributes` bag.
 * A field the row does not have is `null` (`RowMetadata.of` builds one from what is known).
 * Frozen, with its `attributes` and `meta` bags deeply frozen: every reader shares the one
 * value the store keeps, and a write keeps a new one (`RowMetadataStore.patch` /
 * `patchAttributes`).
 */
export interface RowMetadata {
  readonly label: Option<string>;
  readonly heightPx: Option<number>;
  /** Lookup-key column (`:key` or `:~reserved`). Mutable per-row. */
  readonly lookupKey: Option<string>;
  /**
   * Formula expression / Lisp source for formula rows (wire type
   * `"formula"` → `FormulaDataSource`). The descriptor describes the
   * row's shape (dtype / cardinality); this field holds the user-
   * editable expression text. Presence of this field on a non-
   * formula row is meaningless — only `FormulaDataSource.expression`
   * reads it.
   */
  readonly formula: Option<string>;
  /**
   * Free-form attribute bag — subclass-specific keys live here.
   * JSON only: each consuming variant narrows it with the `Json`
   * readers or a decoder (`ModelRow.attributesAs`). List-row
   * `filter`, canvas-row axis config, renderer attrs, and
   * variant-specific extras all live in this bag. This is NOT a
   * `CellValue` bag — cell payloads are a different concern.
   *
   * Mirrors Rust `SheetRowDb.attributes` (JSONB column).
   */
  readonly attributes: Option<JsonObject>;
  /**
   * The closures a preset-expanded row carries beside its attributes
   * (`ChildSpec.graph`: a data canvas's x-domain mapping, a builtin
   * series' extractors). Runtime-only — never serialized.
   */
  readonly graph: Option<Graph.Hooks>;
  /**
   * Free-form metadata bag — system-owned keys (server-declared
   * `encoding`, origin markers, sync bookkeeping, …). Distinct
   * from `attributes` semantically: `attributes` is what a row
   * *is* (user-visible config), `meta` is how the row is
   * *managed* (server/runtime bookkeeping).
   *
   * Mirrors Rust `SheetRowDb.metadata` (JSONB column). Read-only
   * from the client's perspective; server-authoritative.
   */
  readonly meta: Option<JsonObject>;
}

/** A row's metadata from what is known of it (`RowMetadata.of`). */
export namespace RowMetadata {
  /** What `of` takes: whatever is known (left out: `null`). */
  export interface Draft {
    readonly label?: string;
    readonly heightPx?: number;
    readonly lookupKey?: string;
    readonly formula?: string;
    readonly attributes?: JsonObject;
    readonly graph?: Graph.Hooks;
    readonly meta?: JsonObject;
  }

  /**
   * The metadata `draft` describes, frozen, with its bags deeply frozen
   * copies (the draft's own objects stay as they were); a field it leaves
   * out is `null`.
   */
  export function of(draft: Draft): RowMetadata {
    return frozen({
      label: draft.label ?? null,
      heightPx: draft.heightPx ?? null,
      lookupKey: draft.lookupKey ?? null,
      formula: draft.formula ?? null,
      attributes: draft.attributes ?? null,
      graph: draft.graph ?? null,
      meta: draft.meta ?? null,
    });
  }

  /**
   * `metadata` as a store keeps it: frozen, with its `attributes` and `meta`
   * bags deeply frozen (a bag already frozen is kept as it is).
   */
  export function frozen(metadata: RowMetadata): RowMetadata {
    return Object.freeze({
      ...metadata,
      attributes: metadata.attributes == null ? null : frozenCopy(metadata.attributes),
      meta: metadata.meta == null ? null : frozenCopy(metadata.meta),
    });
  }
}

export class RowMetadataStore {
  static readonly ACTOR = new Actor("RowMetadataStore");

  readonly #byRow = new Map<RowLocalId, RowMetadata>();

  readonly #ledger: Option<LedgerBumper>;
  readonly #sink: InvalidationSink;
  constructor(
    sink: InvalidationSink,
    ledger: Option<LedgerBumper>,
  ) {
    this.#sink = sink;
    this.#ledger = ledger;
  }

  /** Read the current metadata. Returns an empty object if unset. */
  get(rowId: RowLocalId): RowMetadata {
    return this.#byRow.get(rowId) ?? EMPTY;
  }

  /** Seed initial metadata (e.g. from hydrate), kept frozen (`RowMetadata.frozen`). Does NOT emit. */
  init(rowId: RowLocalId, metadata: RowMetadata): void {
    this.#byRow.set(rowId, RowMetadata.frozen(metadata));
  }

  /**
   * LR1: bump the row's `attr:<serial>` VALUES node once per metadata patch
   * that actually changed something. Placement-keyed (`serial`) because rows
   * may share one lookupKey as separate views with distinct attribute bags
   * (§3.1). No-op without a ledger binding. `init` does NOT bump (hydrate
   * seed). Coarse by design — any metadata field change (attributes, label,
   * heightPx, …) advances the one attr node; over-bumping is monotonic-safe.
   */
  #bumpAttr(rowId: RowLocalId, changedFields: readonly string[]): void {
    this.#ledger?.bumpOp(
      LedgerKeys.attr(rowId.serial),
      DependencyAspect.VALUES,
      "attr",
      { t: "attr/patch", fields: changedFields },
    );
  }

  /**
   * Field-wise merge patch into a new frozen record (the previous one is
   * left as readers hold it). Emits one Invalidation per field that changes.
   */
  patch(rowId: RowLocalId, patch: Partial<RowMetadata>): void {
    const keys = Object.keys(patch);
    if (keys.length === 0) return;
    const prev = this.get(rowId);
    const merged: RowMetadata = RowMetadata.frozen({ ...prev, ...patch });
    const changedFields = RowMetadataStore.#changed(prev, patch, keys);
    if (changedFields.length === 0) return;
    this.#byRow.set(rowId, merged);
    for (const field of changedFields) {
      this.#sink.emit(eventFor(rowId, field, prev, merged));
    }
    this.#bumpAttr(rowId, changedFields);
  }

  /**
   * Write `patch`'s fields into a new frozen record SILENTLY — the row-field
   * ledger's show (`RowWrites`), which announces each changed field once
   * itself. The render-skip ledger is bumped as `patch` bumps it. Answers the
   * fields that changed.
   */
  show(rowId: RowLocalId, patch: Partial<RowMetadata>): readonly string[] {
    const keys = Object.keys(patch);
    if (keys.length === 0) return [];
    const prev = this.get(rowId);
    const changedFields = RowMetadataStore.#changed(prev, patch, keys);
    if (changedFields.length === 0) return [];
    this.#byRow.set(rowId, RowMetadata.frozen({ ...prev, ...patch }));
    this.#bumpAttr(rowId, changedFields);
    return changedFields;
  }

  /** The `keys` of `patch` whose value differs from `prev`'s. */
  static #changed(prev: RowMetadata, patch: Partial<RowMetadata>, keys: readonly string[]): string[] {
    const p = prev as Readonly<Record<string, unknown>>;
    const n = patch as Readonly<Record<string, unknown>>;
    return keys.filter((k) => p[k] !== n[k]);
  }

  /**
   * Replace the row's `attributes` bag with `bag` SILENTLY — the row-field
   * ledger's show of an attribute change (`RowWrites`), which announces each
   * changed key once itself. `keys` are the keys that changed: the
   * render-skip ledger is bumped with them, as `patchAttributes` bumps.
   */
  showAttributes(rowId: RowLocalId, bag: Option<JsonObject>, keys: readonly string[]): void {
    const prev = this.get(rowId);
    this.#byRow.set(rowId, RowMetadata.frozen({ ...prev, attributes: bag }));
    if (keys.length > 0) this.#bumpAttr(rowId, keys);
  }

  /**
   * Merge `patch` into a new frozen copy of the row's `attributes` bag, key
   * by key (the bag readers already hold is left as it is).
   * Emits `RowDataChanged(rowId, field)` once per key whose value
   * actually changed — finer-grained than the whole-`attributes`
   * event `patch({ attributes })` would produce. Use this when
   * callers care about specific attribute keys (renderer attrs,
   * variant-specific fields).
   */
  patchAttributes(
    rowId: RowLocalId,
    patch: JsonObject,
  ): void {
    const keys = Object.keys(patch);
    if (keys.length === 0) return;
    const prev = this.get(rowId);
    const prevAttrs = prev.attributes ?? EMPTY_ATTRS;
    const nextAttrs: { [key: string]: Json } = { ...prevAttrs };
    const changedFields: string[] = [];
    for (const k of keys) {
      const v = patch[k];
      if (v === undefined) continue;
      if (prevAttrs[k] !== v) {
        nextAttrs[k] = frozenCopy(v);
        changedFields.push(k);
      }
    }
    if (changedFields.length === 0) return;
    this.#byRow.set(rowId, Object.freeze({ ...prev, attributes: Object.freeze(nextAttrs) }));
    for (const field of changedFields) {
      this.#sink.emit(
        new RowDataChanged(RowMetadataStore.ACTOR, rowId, field),
      );
    }
    this.#bumpAttr(rowId, changedFields);
  }
}

function eventFor(
  rowId: RowLocalId,
  field: string,
  prev: RowMetadata,
  next: RowMetadata,
):
  | RowHeightChanged
  | RowLegendChanged
  | RowDataChanged {
  if (field === "heightPx") {
    return new RowHeightChanged(RowMetadataStore.ACTOR, rowId);
  }
  if (field === "label") {
    return new RowLegendChanged(
      RowMetadataStore.ACTOR,
      rowId,
      prev.label ?? null,
      next.label ?? null,
    );
  }
  return new RowDataChanged(RowMetadataStore.ACTOR, rowId, field);
}

const EMPTY: RowMetadata = Object.freeze(RowMetadata.of({}));
const EMPTY_ATTRS: JsonObject = Object.freeze({});
